// **The bee-type bar's counts, read off the playhead** (design §3.4).
//
// A chip's number is not "how many of this type the colony has" — a document-derived
// figure that would sit still while the player scrubs. It is *this tick's* living count,
// derived from the resident trace record's per-bee `typeId`, so dragging the playhead
// animates the colony's composition. A composition collapsing at 6:00 is exactly the
// diagnosis the screen exists to surface, and it is only visible if the counts move.
//
// Two rules make that honest, and both are spec requirements rather than preferences:
//
//   * **Absence is a placeholder, never a retained value.** When no record is resident for
//     the playhead position — a range still in flight, or the coarse-only tier, which
//     carries no per-type breakdown at all (gap **G-A**) — this module returns an
//     explicitly unavailable population. The unavailability is a *discriminant in the
//     type*, not a `0` and not an empty map, because both of those are real answers a
//     colony can legitimately have, and a caller that cannot tell them apart is one
//     refactor away from painting a stale composition as a current one.
//   * **The queen is excluded.** She is `typeId: null` in a detail record, and
//     `review-world.ts` (~line 76) already drops her from the bee set for the same reason
//     (**D-091**): she is typeless, drawn from the hive anchor, and belongs to no chip.
//     The total reported here is therefore the same set the chips sum to, which is why the
//     `bees` counter and the bar can never disagree — they are one traversal apart.
//
// Pure: a record in, plain data out. No React, no DOM, no trace reader.

import type { DetailRecord } from '../../net/rounds-trace';

/** A population the playhead actually has a source for. */
export interface ResidentPopulation {
  readonly available: true;
  /** Living count per `ColonyTypeId`. A type with no living bees is simply absent from
   * the map — ask through {@link countForType}, which reads that as the real `0` it is. */
  readonly byType: ReadonlyMap<number, number>;
  /** Every living non-queen bee counted above, so the `bees` counter agrees with the
   * chips by construction rather than by a second, drifting traversal. */
  readonly total: number;
}

/** No source at the playhead. Every readout derived from it renders its placeholder. */
export interface UnavailablePopulation {
  readonly available: false;
  readonly byType: null;
  readonly total: null;
}

export type PlayheadPopulation = ResidentPopulation | UnavailablePopulation;

/** The single unavailable value. Shared rather than reconstructed so an identity check is
 * a legitimate way to spot it, and so no caller can build a half-populated variant. */
export const POPULATION_UNAVAILABLE: UnavailablePopulation = {
  available: false,
  byType: null,
  total: null,
};

/**
 * The living, non-queen population at the playhead.
 *
 * `record` is whatever the trace reader answered for the current tick — pass its `null`
 * straight through rather than skipping the call, so the placeholder decision is made
 * here once instead of at every readout.
 *
 * `colony` scopes the count to one colony. A sandbox run is the player's colony alone
 * (ROUNDS-MODE **G3**), so the screen may omit it; it exists so that the same mapping over
 * a multi-colony trace counts the player's own bees rather than the whole field's.
 */
export function playheadPopulation(record: DetailRecord, colony?: number): ResidentPopulation;
export function playheadPopulation(
  record: DetailRecord | null,
  colony?: number,
): PlayheadPopulation;
export function playheadPopulation(
  record: DetailRecord | null,
  colony?: number,
): PlayheadPopulation {
  if (record === null) {
    return POPULATION_UNAVAILABLE;
  }

  const byType = new Map<number, number>();
  let total = 0;
  for (const bee of record.bees) {
    if (bee.typeId === null) {
      continue; // the typeless queen: no chip owns her (D-091, as in `review-world.ts`)
    }
    if (colony !== undefined && bee.colony !== colony) {
      continue;
    }
    byType.set(bee.typeId, (byType.get(bee.typeId) ?? 0) + 1);
    total += 1;
  }
  return { available: true, byType, total };
}

/**
 * One type's count at the playhead: the living count, `0` when the type exists but none
 * of its bees are alive at this tick, and `null` — the placeholder — only when the
 * playhead has no source at all. The distinction is the whole point: a type that has died
 * out reads as the zero it is, and is never quietly conflated with a missing record.
 */
export function countForType(population: PlayheadPopulation, typeId: number): number | null {
  if (!population.available) {
    return null;
  }
  return population.byType.get(typeId) ?? 0;
}
